import { useCallback, useMemo, useState } from "react";
import {
    Entity,
    EntityProfile,
    EntityProfileIndustry,
} from "@common/types/entity";
import {
    AccountantOnboardingRoute,
    getAccountantOnboardingRoute,
} from "@common/billing";
import { Organization } from "@common/types/organization";
import { UserSignupPurpose } from "@common/types/user";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useUser } from "@hooks/useUser";
import { useBillingStatus } from "@hooks/useBillingStatus";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { useMobileBlockScreen } from "@hooks/useMobileView";
import { usePromise } from "@hooks/usePromise";
import { useOrganization } from "@api/organization.api";
import { useEditEntityMutation } from "@api/entity.api";
import { useUpdateUserMutation } from "@api/user.api";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { ProfileFormSchema } from "@components/onboarding/useEntityProfileFormConfig";
import { trackEvent } from "@lib/analytics";
import { PrimaryFlowSteps } from "../flowSteps";
import { Purpose } from "../steps/Purpose/Purpose";
import { Profile } from "../steps/Profile";
import { ConnectAccounts } from "../steps/ConnectAccounts/ConnectAccounts";
import { ConnectAccountsSidebar } from "../steps/ConnectAccounts/ConnectAccountsSidebar";
import { PaymentProcessor } from "../steps/PaymentProcessor/PaymentProcessor";
import { PaymentProcessorSidebar } from "../steps/PaymentProcessor/PaymentProcessorSidebar";
import { ChoosePlan } from "../steps/ChoosePlan";
import { Outro } from "../steps/Outro/Outro";
import { AccountantOutro } from "../steps/Outro/AccountantOutro";
import { PlanManagementProvider } from "../../billing/PlanManagement/PlanManagementProvider";
import { WorkspacePreview } from "../components/WorkspacePreview/WorkspacePreview";
import { WaitlistOutro } from "../steps/WaitlistOutro";
import { FirmInfo, type FirmInfoValues } from "../steps/FirmInfo";
import { FirmSetup, type FirmSetupValues } from "../steps/FirmSetup";
import { AccountantPartnerCall } from "../steps/AccountantPartnerCall/AccountantPartnerCall";
import { AccountantPartnerCallOutro } from "../steps/AccountantPartnerCallOutro/AccountantPartnerCallOutro";
import { OnboardingMobileBlock } from "../steps/OnboardingMobileBlock";
import { EnterpriseCall } from "../steps/EnterpriseCall";
import { FreshbooksSidebar } from "../components/WorkspacePreview/FreshbooksSidebar";
import { FlowContext } from "../flowContext";
import { flowResolver } from "../flowResolver";
import { OrganizationPreview } from "../components/OrganizationPreview/OrganizationPreview";

interface UseStepContentParams {
    currentStep: PrimaryFlowSteps;
    entity: Entity;
    onFinish: () => Promise<void>;
    go: (step: PrimaryFlowSteps) => void;
}

export function useStepContent({
    currentStep,
    entity,
    onFinish,
    go,
}: UseStepContentParams) {
    const user = useUser();
    const workspace = useActiveWorkspace();
    const allFinancialAccounts = useFinancialAccounts();
    const { isSubscribed } = useBillingStatus();
    const isMobile = useMobileBlockScreen();
    const updateEntityMutation = useEditEntityMutation(entity);
    const updateUserMutation = useUpdateUserMutation();
    const { data: organization } = useOrganization();

    const isFreshbooksCustomer = workspace?.isFreshbooksCustomer ?? false;

    // Held between the two firm steps; the organization is created in FirmSetup
    const [firmInfoValues, setFirmInfoValues] = useState<FirmInfoValues | null>(
        null,
    );
    const [firmSetupValues, setFirmSetupValues] =
        useState<FirmSetupValues | null>(null);

    // Store organization created in FirmSetup step to pass to Outro
    const [newlyCreatedOrganization, setNewlyCreatedOrganization] =
        useState<Organization | null>(organization ?? null);

    const accountantCallRoute = getAccountantOnboardingRoute(
        newlyCreatedOrganization?.profile,
    );

    // Store profile form values for WorkspacePreview sidebar
    const [profileFormValues, setProfileFormValues] =
        useState<ProfileFormSchema | null>(null);

    // Build FlowContext
    const context: FlowContext = useMemo<FlowContext>(
        () => ({
            user,
            entity,
            workspace,
            organization: organization ?? null,
            financialAccounts: allFinancialAccounts,
            isSubscribed,
            isMobile,
        }),
        [
            user,
            entity,
            workspace,
            allFinancialAccounts,
            organization,
            isSubscribed,
            isMobile,
        ],
    );

    // Navigation helpers - accept optional contextOverride so callers can pass
    // freshly updated user/entity when the query cache hasn't triggered a re-render yet
    const goNextStep = useCallback(
        (step: PrimaryFlowSteps, contextOverride?: FlowContext) => {
            const ctx = contextOverride ?? context;
            const nextStep = flowResolver.next(step, ctx) ?? "outro";
            go(nextStep);
        },
        [go, context],
    );

    const goBackStep = useCallback(
        (step: PrimaryFlowSteps) => {
            const backStep = flowResolver.prev(step, context) ?? "purpose";
            // The firm info draft is in memory only; after a reload, FirmSetup can't render without it
            if (backStep === "firmSetup" && !firmInfoValues) {
                go("firmInfo");
                return;
            }
            go(backStep);
        },
        [go, context, firmInfoValues],
    );

    // Entity profile update helper
    const handleEntityProfileUpdate = useCallback(
        async (payload: Partial<EntityProfile>) =>
            await updateEntityMutation.mutateAsync({
                profile: {
                    ...entity.profile,
                    ...payload,
                },
            }),
        [entity.profile, updateEntityMutation],
    );

    // Handlers
    const handlePaymentProcessors = useCallback(
        async (requestedProcessors: string) => {
            await handleEntityProfileUpdate({
                paymentProcessorRequest: requestedProcessors,
            });
            void trackEvent(AnalyticsEvent.ONBOARDING_PAYMENT_SCREEN);

            goNextStep("paymentProcessor");
        },
        [handleEntityProfileUpdate, goNextStep],
    );

    const handleOnboardingPaymentInitiated = useCallback(
        (plan: FLAT_RATE_PLANS) => {
            void trackEvent(AnalyticsEvent.ONBOARDING_PAYMENT_INITIATED, {
                plan,
            });
        },
        [],
    );

    const handleProfileSubmit = useCallback(
        async ({
            firstName,
            lastName,
            name,
            legalType,
            country,
            source,
            industries,
            industryOther,
            annualRevenue,
            unverifiedPhoneNumber,
        }: ProfileFormSchema) => {
            const isAccountantOrBookkeeper = industries?.some(
                (i) =>
                    i === EntityProfileIndustry.ACCOUNTANT ||
                    i === EntityProfileIndustry.BOOKKEEPER,
            );
            const signupPurpose = isAccountantOrBookkeeper
                ? UserSignupPurpose.ACCOUNTANT
                : UserSignupPurpose.BUSINESS_OWNER;

            // Re-submitting the profile step (going back from a later step and
            // continuing) must not re-fire the signup event — it re-triggers the
            // welcome email campaign in Customer.io. PRO-12915
            const hasSubmittedApplication =
                user.eventsTriggered?.applicationSubmitted === true;

            await updateUserMutation.mutateAsync({
                preferredName: `${firstName} ${lastName}`,
                unverifiedPhoneNumber,
                signupPurpose,
                eventsTriggered: {
                    ...user.eventsTriggered,
                    applicationSubmitted: true,
                },
            });

            // Fired right after the request that persists the flag, so a
            // failure later in the chain leaves neither of them set and a
            // retry still sends the event.
            if (!hasSubmittedApplication) {
                void trackEvent(AnalyticsEvent.SIGNUP_APPLICATION_SUBMITTED, {
                    name,
                    industries,
                    industryOther,
                    annualRevenue,
                    country,
                    source,
                });
            }

            const updatedEntity = await updateEntityMutation.mutateAsync({
                name,
                legalType,
                profile: {
                    ...entity.profile,
                    industries,
                    industryOther,
                    annualRevenue,
                    country,
                    source,
                },
            });

            if (isMobile) {
                go("mobileBlock");
                return;
            }

            // After profile update, recalculate context and navigate to next step
            const updatedContext: FlowContext = {
                ...context,
                user: {
                    preferredName: `${firstName} ${lastName}`,
                    signupPurpose,
                    bookedEnterpriseCall: user.bookedEnterpriseCall ?? false,
                },
                entity: updatedEntity,
            };
            const calculatedNextStep = flowResolver.next(
                "profile",
                updatedContext,
            );
            if (calculatedNextStep) {
                go(calculatedNextStep);
            }
        },
        [
            updateUserMutation,
            updateEntityMutation,
            entity.profile,
            isMobile,
            user,
            context,
            go,
        ],
    );

    // beforeFirstConnection state management
    const [connectPersonalReminder, setConnectPersonalReminder] =
        useState(false);
    const [reminderPromise, { resolve: confirmReminder }] = usePromise<void>();

    const beforeFirstConnection = useCallback(() => {
        if (allFinancialAccounts.length > 0) {
            confirmReminder();
        } else {
            setConnectPersonalReminder(true);
            reminderPromise.then(() => setConnectPersonalReminder(false));
        }
        return reminderPromise;
    }, [confirmReminder, allFinancialAccounts.length, reminderPromise]);

    const handleReminderConfirmed = useCallback(async () => {
        setConnectPersonalReminder(false);
        // For smooth animation between closing confirmation modal and opening accounts modal
        await new Promise((resolve) => setTimeout(resolve, 500));
        confirmReminder();
    }, [confirmReminder]);

    const content = useMemo(() => {
        let mainContent: React.ReactNode;
        let sidebarContent: React.ReactNode;

        switch (currentStep) {
            case "purpose":
                mainContent = (
                    <Purpose
                        onContinue={(updatedUser) =>
                            goNextStep(
                                "purpose",
                                updatedUser
                                    ? { ...context, user: updatedUser }
                                    : undefined,
                            )
                        }
                    />
                );
                break;

            case "profile":
                mainContent = (
                    <Profile
                        entity={entity}
                        user={user}
                        onSubmit={handleProfileSubmit}
                        onBack={() => goBackStep("profile")}
                        onValuesChange={setProfileFormValues}
                    />
                );
                sidebarContent = isFreshbooksCustomer ? (
                    <FreshbooksSidebar />
                ) : (
                    <WorkspacePreview
                        formValues={profileFormValues ?? undefined}
                    />
                );
                break;

            case "connectAccounts":
                mainContent = (
                    <ConnectAccounts
                        onBack={() => goBackStep("connectAccounts")}
                        onFinished={() => goNextStep("connectAccounts")}
                        beforeConnect={beforeFirstConnection}
                        buttonSize="lg"
                    />
                );
                sidebarContent = <ConnectAccountsSidebar />;
                break;

            case "paymentProcessor":
                mainContent = (
                    <PaymentProcessor
                        initialValue={
                            entity.profile?.paymentProcessorRequest ?? ""
                        }
                        financialAccounts={allFinancialAccounts}
                        onSubmit={handlePaymentProcessors}
                        back={() => goBackStep("paymentProcessor")}
                    />
                );
                sidebarContent = <PaymentProcessorSidebar />;
                break;

            case "choosePlan":
                mainContent = (
                    <PlanManagementProvider
                        onBeforeUpgrade={handleOnboardingPaymentInitiated}
                    >
                        <ChoosePlan
                            onContinue={() => goNextStep("choosePlan")}
                            onEnterpriseCall={() => go("enterpriseCall")}
                            back={() => goBackStep("choosePlan")}
                        />
                    </PlanManagementProvider>
                );
                break;

            case "enterpriseCall":
                mainContent = (
                    <EnterpriseCall
                        onContinue={() => goNextStep("enterpriseCall")}
                        back={() => goBackStep("enterpriseCall")}
                    />
                );
                break;

            case "outro":
                mainContent = <Outro onFinished={onFinish} />;
                break;

            case "accountantOutro":
                if (newlyCreatedOrganization) {
                    mainContent = (
                        <AccountantOutro
                            organization={newlyCreatedOrganization}
                        />
                    );
                }
                break;

            case "accountantPartnerCall":
                if (
                    newlyCreatedOrganization &&
                    accountantCallRoute !== AccountantOnboardingRoute.SELF_SERVE
                ) {
                    mainContent = (
                        <AccountantPartnerCall
                            route={accountantCallRoute}
                            back={() => goBackStep("accountantPartnerCall")}
                            onContinue={(contextOverride) => {
                                const mergedContext: FlowContext =
                                    contextOverride
                                        ? {
                                              ...context,
                                              user: {
                                                  ...context.user,
                                                  ...(contextOverride.user ??
                                                      {}),
                                              },
                                          }
                                        : context;
                                goNextStep(
                                    "accountantPartnerCall",
                                    mergedContext,
                                );
                            }}
                        />
                    );
                }
                break;

            case "accountantPartnerCallOutro":
                mainContent = <AccountantPartnerCallOutro />;
                sidebarContent = <OrganizationPreview />;
                break;

            case "waitlistOutro":
                mainContent = (
                    <WaitlistOutro onBack={() => goBackStep("waitlistOutro")} />
                );
                sidebarContent = (
                    <WorkspacePreview
                        formValues={profileFormValues ?? undefined}
                    />
                );
                break;

            case "firmInfo":
                mainContent = (
                    <FirmInfo
                        entity={entity}
                        initialValues={firmInfoValues}
                        back={() => goBackStep("firmInfo")}
                        next={(values: FirmInfoValues) => {
                            setFirmInfoValues(values);
                            goNextStep("firmInfo");
                        }}
                    />
                );
                sidebarContent = <OrganizationPreview />;
                break;

            case "firmSetup":
                mainContent = firmInfoValues ? (
                    <FirmSetup
                        entity={entity}
                        firmInfo={firmInfoValues}
                        initialValues={firmSetupValues}
                        back={(values: FirmSetupValues) => {
                            setFirmSetupValues(values);
                            goBackStep("firmSetup");
                        }}
                        next={(passingOrganization: Organization | null) => {
                            if (passingOrganization) {
                                void trackEvent(
                                    AnalyticsEvent.FIRM_APPLICATION_SUBMITTED,
                                    {
                                        businessName: passingOrganization.name,
                                        firmSize:
                                            passingOrganization.profile
                                                ?.firmSize,
                                        servicesOffered:
                                            passingOrganization.profile
                                                ?.servicesOffered ?? [],
                                        clientCountries:
                                            passingOrganization.clientCountries ??
                                            [],
                                    },
                                );
                            }

                            setNewlyCreatedOrganization(passingOrganization);
                            const updatedContext: FlowContext = {
                                ...context,
                                organization: passingOrganization ?? null,
                                entity: {
                                    ...context.entity,
                                    profile: {
                                        ...context.entity.profile,
                                        firmSize:
                                            passingOrganization?.profile
                                                ?.firmSize,
                                    },
                                },
                            };
                            goNextStep("firmSetup", updatedContext);
                        }}
                    />
                ) : null;
                sidebarContent = <OrganizationPreview />;
                break;

            case "mobileBlock":
                mainContent = (
                    <OnboardingMobileBlock heading="Application accepted" />
                );
                break;
        }

        return { main: mainContent, sidebar: sidebarContent };
    }, [
        currentStep,
        isFreshbooksCustomer,
        entity,
        user,
        context,
        allFinancialAccounts,
        handlePaymentProcessors,
        handleOnboardingPaymentInitiated,
        handleProfileSubmit,
        onFinish,
        goBackStep,
        goNextStep,
        go,
        beforeFirstConnection,
        newlyCreatedOrganization,
        accountantCallRoute,
        profileFormValues,
        firmInfoValues,
        firmSetupValues,
    ]);

    return {
        ...content,
        connectPersonalReminder,
        handleReminderConfirmed,
    };
}
